/**
 * Connectivity tracking behind the offline screen.
 *
 * @capacitor/network is deliberately NOT a dependency here — it is not installed,
 * and adding it would buy little: on iOS it reports the radio state, which says
 * nothing about whether our API is reachable (captive portals, airplane-mode-off-
 * but-no-signal, backend down all read as "connected"). Two cheap signals cover
 * the App Review airplane-mode test and the real-world cases:
 *
 *   1. `navigator.onLine` — instant, and the only thing that flips the moment the
 *      user toggles airplane mode.
 *   2. Consecutive request-level failures on the shared axios instance — catches
 *      the cases navigator.onLine lies about.
 *
 * One failure is not enough to blank the app (a single request can die for its own
 * reasons), hence FAILURE_THRESHOLD.
 */
import { useCallback, useEffect, useState } from 'react';

import axios from 'axios';

/** Consecutive network-level failures before we call the app offline. */
export const FAILURE_THRESHOLD = 2;

type NetworkListener = (online: boolean) => void;

const listeners = new Set<NetworkListener>();
let consecutiveFailures = 0;
let windowListenersBound = false;
let lastNotified = true;

/**
 * True when the error never reached a server. A response of any status — even
 * 500 — proves the network is up, so those must not count.
 */
export function isNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { response?: unknown; code?: string; message?: string };
  if (candidate.response) return false;
  if (candidate.code === 'ERR_CANCELED') return false; // user/route abort, not connectivity
  return (
    candidate.code === 'ERR_NETWORK' ||
    candidate.code === 'ECONNABORTED' ||
    candidate.message === 'Network Error'
  );
}

function browserOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** The app's current verdict: the browser flag AND our own failure streak. */
export function isOnline(): boolean {
  return browserOnline() && consecutiveFailures < FAILURE_THRESHOLD;
}

function emit(): void {
  const online = isOnline();
  if (online === lastNotified) return;
  lastNotified = online;
  listeners.forEach((listener) => listener(online));
}

/** Any successful response clears the streak — we are demonstrably online. */
export function reportRequestSuccess(): void {
  consecutiveFailures = 0;
  emit();
}

export function reportRequestFailure(error: unknown): void {
  if (!isNetworkError(error)) return;
  consecutiveFailures += 1;
  emit();
}

/** Subscribes to online/offline transitions. Returns the unsubscribe function. */
export function subscribeToNetworkStatus(listener: NetworkListener): () => void {
  listeners.add(listener);
  // Bound once for the life of the page and never removed: they are the only
  // instant signal for an airplane-mode toggle, and there is exactly one store.
  if (!windowListenersBound && typeof window !== 'undefined') {
    windowListenersBound = true;
    window.addEventListener('online', reportRequestSuccess);
    window.addEventListener('offline', emit);
  }
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Installs the axios response interceptor that feeds failure detection.
 * Returns an eject function for the caller's cleanup.
 */
export function installNetworkInterceptor(): () => void {
  const id = axios.interceptors.response.use(
    (response) => {
      reportRequestSuccess();
      return response;
    },
    (error) => {
      reportRequestFailure(error);
      return Promise.reject(error);
    }
  );
  return () => axios.interceptors.response.eject(id);
}

/**
 * /health sits at the server root, not under /api, so strip the suffix the same
 * way components/CalendarFeed.tsx does for feed URLs.
 */
export function healthProbeUrl(baseUrl: string | undefined = axios.defaults.baseURL || ''): string {
  return `${baseUrl.replace(/\/api\/?$/, '')}/health`;
}

/**
 * Actively re-checks connectivity — what the Retry button runs.
 *
 * Uses fetch rather than axios on purpose: axios carries the auth interceptor
 * that logs the user out on a 401, and a connectivity probe must never be able to
 * sign anyone out. Any HTTP response counts as online; only a thrown request
 * counts as offline.
 */
export async function probeConnectivity(fetchImpl?: typeof fetch): Promise<boolean> {
  const doFetch = fetchImpl ?? (typeof fetch === 'function' ? fetch : null);
  if (!doFetch) return isOnline();

  try {
    await doFetch(`${healthProbeUrl()}?_=${Date.now()}`, { method: 'GET', cache: 'no-store' });
    reportRequestSuccess();
    return true;
  } catch {
    consecutiveFailures = FAILURE_THRESHOLD;
    emit();
    return false;
  }
}

export interface NetworkStatus {
  online: boolean;
  /** True while a manual retry is in flight, for the button's spinner. */
  checking: boolean;
  retry: () => Promise<boolean>;
}

export function useNetworkStatus(): NetworkStatus {
  const [online, setOnline] = useState<boolean>(isOnline);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    setOnline(isOnline());
    return subscribeToNetworkStatus(setOnline);
  }, []);

  const retry = useCallback(async () => {
    setChecking(true);
    try {
      const reachable = await probeConnectivity();
      setOnline(reachable);
      return reachable;
    } finally {
      setChecking(false);
    }
  }, []);

  return { online, checking, retry };
}
